import { type ReactElement, useState } from "react"
import styled from "styled-components"
import { DownloadsSection } from "~/features/downloads/DownloadsSection"
import { AccessWindowProvider } from "~/features/entitlements/AccessWindowContext"
import { EntitlementSections } from "~/features/entitlements/components/EntitlementSections"
import { ManageAccessButton } from "~/features/entitlements/components/ManageAccessButton"
import { RedeemCodeSection } from "~/features/entitlements/components/RedeemCodeSection"
import { PocOptionsPanel, PocOptionsProvider, usePocOptions } from "~/features/poc"
import { ProvenanceLegend, ProvenanceProvider } from "~/features/provenance"
import { useDigitalEventLibrary } from "../api/useDigitalEventLibrary"
import { useEbookLibrary } from "../api/useEbookLibrary"
import { useStudyLibrary } from "../api/useStudyLibrary"
import { DEFAULT_FILTERS, isSectionVisible, type LibraryFilters } from "../filters"
import { CardColorLegend } from "./CardColorLegend"
import { LibraryFilterBar } from "./LibraryFilterBar"
import { LibrarySection } from "./LibrarySection"

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`

const PageHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`

const PageHeading = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme }) => theme.color.textPrimary};
`

/**
 * POC (proposal 03). With the local MLC facade running, the commerce-backed
 * sections render real rows; without it, the honest placeholder stands, because
 * the deployed graph has no `entitlements` field to ask for.
 */
const showEntitlements = Boolean(import.meta.env.VITE_POC_FACADE)

if (import.meta.env.DEV && !showEntitlements) {
  console.info(
    "[digital-library] commerce sections are OFF (VITE_POC_FACADE unset) — the Downloads placeholder is expected. Run `pnpm --filter web poc:up`. See POC.md.",
  )
}

/**
 * The Digital Library content region. The header, left nav, breadcrumbs and
 * footer in the Figma frame belong to the host shell and are deliberately absent.
 */
const DigitalLibraryContent = (): ReactElement => {
  const [filters, setFilters] = useState<LibraryFilters>(DEFAULT_FILTERS)
  const { dataSources, layout } = usePocOptions()

  /**
   * The provider wraps the whole page so the library cards can read the seat
   * window — the expiry pill and the per-row Manage Access link — which lives on
   * the commerce rows and not on `libraryV2`. With the facade off the index is
   * empty and the cards are unchanged.
   */
  return (
    <ProvenanceProvider isOn={dataSources && layout === "grid"}>
      <AccessWindowProvider>
        <Page data-qa-hook="digital-library-page" data-poc-facade={showEntitlements ? "on" : "off"}>
          <PageHeader>
            <PageHeading>Digital Library</PageHeading>
            <HeaderActions>
              <PocOptionsPanel />
              <ManageAccessButton />
            </HeaderActions>
          </PageHeader>

          {/* Both keys sit under the options that turn them on, so a switch and its explanation stay together. */}
          <CardColorLegend />
          <ProvenanceLegend />

          {/*
            Above the library, as MLC has it: redeeming is what someone arrives
            on this page to DO, and the confirmation it renders is a list of new
            items that should sit next to the control that produced them rather
            than below three sections of things they already had. Gated with the
            rest of the commerce half — the deployed graph has no `redeemCode`.
          */}
          {showEntitlements ? <RedeemCodeSection /> : null}

          <LibraryFilterBar filters={filters} onChange={setFilters} />

          {/* Unmounted rather than hidden when filtered out, so a section the customer is not looking at stops paging. */}
          {isSectionVisible(filters, "studies") ? (
            <LibrarySection
              title="Studies"
              slug="studies"
              emptyMessage="You have no studies in your library."
              useLibrary={useStudyLibrary}
              filters={filters}
            />
          ) : null}
          {isSectionVisible(filters, "events") ? (
            <LibrarySection
              title="Digital Events"
              slug="events"
              emptyMessage="You have no digital events in your library."
              useLibrary={useDigitalEventLibrary}
              filters={filters}
            />
          ) : null}
          {isSectionVisible(filters, "ebooks") ? (
            <LibrarySection
              title="Ebooks"
              slug="ebooks"
              emptyMessage="You have no ebooks in your library."
              useLibrary={useEbookLibrary}
              filters={filters}
            />
          ) : null}
          {showEntitlements ? <EntitlementSections filters={filters} /> : <DownloadsSection />}
        </Page>
      </AccessWindowProvider>
    </ProvenanceProvider>
  )
}

/**
 * `PocOptionsProvider` sits outside `ProvenanceProvider` so the latter can read
 * `dataSources` from it.
 */
export const DigitalLibraryPage = (): ReactElement => (
  <PocOptionsProvider>
    <DigitalLibraryContent />
  </PocOptionsProvider>
)
